import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, DownloadIcon, ExternalLinkIcon, FileTextIcon, MailIcon, PhoneIcon, ClockIcon, GlobeIcon } from 'lucide-react';
import { SEWP_LOGO, ORDERING_GUIDE_URL, sewpCategories, contractInfo, pmContacts, fairOpportunityIntro, fairOpportunityList, fairOpportunityRest, orderingSupport, orderingGuideContents } from '../data/sewpVI';
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nrs-orange focus-visible:ring-offset-2 rounded-sm';
const linkClass = `font-semibold text-nrs-navy underline underline-offset-2 hover:text-nrs-orange ${focusRing}`;
function ExternalLink({
  href,
  children
}: {
  href: string;
  children: React.ReactNode;
}) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 break-all ${linkClass}`} data-id="element-1873" data-mp="1bbaaxy6wy6u0">
      {children}
      <ExternalLinkIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" data-id="element-1874" data-mp="35pl24dazdd2y" />
      <span className="sr-only" data-id="element-1875" data-mp="3u4eoz21h70ta">(opens in new tab)</span>
    </a>;
}
function SectionHeading({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) {
  return <>
      <h2 id={id} className="text-2xl md:text-3xl font-bold mb-3" data-id="element-1876" data-mp="3p37gt1vf7zm3">
        {children}
      </h2>
      <div className="h-1 w-12 bg-nrs-orange rounded-full mb-6" aria-hidden="true" data-id="element-1877" data-mp="7qwmtd596e64"></div>
    </>;
}
export function NasaSewpPage() {
  return <div className="w-full" data-id="element-1878" data-mp="3b50ria4jf7y1">
      {/* HERO */}
      <section className="section-navy hero-grid-pattern" data-id="element-1879" data-mp="3b5so9o9vfbpw">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1880" data-mp="36790dibpvpuw">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-1881" data-mp="3k68qkuevoct6">
            <ol className="flex flex-wrap items-center gap-1 text-sm font-bold text-nrs-light" data-id="element-1882" data-mp="3k7s4wfgz4dsb">
              {[{
              to: '/',
              label: 'Home'
            }, {
              to: '/about-us',
              label: 'About Us'
            }, {
              to: '/about-us/contract-vehicles',
              label: 'Contract Vehicles'
            }].map(crumb => <li key={crumb.to} className="flex items-center gap-1" data-id="element-1883" data-mp="3o9o7z3si20jp">
                  <Link to={crumb.to} className="hover:text-white underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm" data-id="element-1884" data-mp="3t7zg9a35c2zt">
                    {crumb.label}
                  </Link>
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1885" data-mp="61yslbo9cuxd" />
                </li>)}
              <li aria-current="page" className="text-white" data-id="element-1886" data-mp="35jom0pryh1ki">
                NASA SEWP VI
              </li>
            </ol>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-center gap-6" data-id="element-1887" data-mp="3af3wd7qr8fc6">
            <img src={SEWP_LOGO} alt="NASA SEWP VI Contract Holder logo" className="h-24 w-36 flex-shrink-0 object-contain rounded-md bg-white p-1" data-id="element-1888" data-mp="3ehs7jm2xxycb" />
            <div data-id="element-1889" data-mp="2msfbqead28rv">
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-3" data-id="element-1890" data-mp="2mquicrzs8vvl">
                NASA SEWP VI
              </h1>
              <p className="text-lg text-nrs-light font-bold" data-id="element-1891" data-mp="2qro69ld86aoh">
                Prime contract holder — Categories B &amp; C
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* A. CONTRACT OVERVIEW */}
      <section className="section-white" aria-labelledby="overview" data-id="element-1892" data-mp="2a91j4n9kb18w">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1893" data-mp="2abebzvguyw0q">
          <div className="max-w-3xl" data-id="element-1894" data-mp="259f72h5gzr1o">
            <SectionHeading id="overview" data-id="element-1895" data-mp="25cjfxrtvrayo">Contract Overview</SectionHeading>
            <p className="text-lg text-nrs-gray leading-relaxed mb-8" data-id="element-1896" data-mp="20cf1s8gg2sbm">
              NASA Solutions for Enterprise-Wide Procurement (SEWP, pronounced
              "soup") VI is a multi-award Government-Wide Acquisition Contract
              (GWAC) that enables NASA and all Federal Agencies (and their
              approved contractors) to acquire Information Technology,
              Communication, and Audio-Visual (ITC/AV) solutions and services.
              Created in 1993, SEWP was the first GWAC in the federal
              acquisition space and is self-funded through a 0.34% usage fee.
            </p>
            <p className="text-nrs-gray leading-relaxed mb-5" data-id="element-1897" data-mp="3cq533fgf4g1y">
              SEWP VI is organized into service categories. New River Systems
              holds prime awards in two:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1898" data-mp="37x2hpulsocwu">
            {sewpCategories.map(cat => <div key={cat.code} className="card border-l-4 border-l-nrs-orange" data-id="element-1899" data-mp="2txawp9wv1w9c">
                <p className="text-sm font-bold text-nrs-navy mb-1" data-id="element-1900" data-mp="2tzdyssbaffuf">{cat.code}</p>
                <h3 className="text-xl font-bold mb-2" data-id="element-1901" data-mp="2y07ljiczuolq">{cat.name}</h3>
                <p className="text-nrs-gray leading-relaxed" data-id="element-1902" data-mp="2k057k77t4fbr">{cat.description}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* B. CONTRACT INFORMATION */}
      <section className="section-light-blue" aria-labelledby="contract-info" data-id="element-1903" data-mp="i78opy7no8h3">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1904" data-mp="n6tjrxjpfwoj">
          <SectionHeading id="contract-info" data-id="element-1905" data-mp="n4qyfqthyrq5">NRS Contract Information</SectionHeading>
          <div className="card" data-id="element-1906" data-mp="27pdtbeu5pvwg">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10" data-id="element-1907" data-mp="22psxhdaaa3ny">
              {contractInfo.map(item => <div key={item.label} className="py-4 border-b border-gray-200" data-id="element-1908" data-mp="2hep210d4syjt">
                  <dt className="text-sm font-bold text-nrs-navy mb-1" data-id="element-1909" data-mp="2hgs109w9i20o">{item.label}</dt>
                  <dd className="text-nrs-gray" data-id="element-1910" data-mp="2cmnt9of69pyj">{item.value}</dd>
                </div>)}
              <div className="py-4 md:col-span-2" data-id="element-1911" data-mp="2qe2gy8k0yfbx">
                
                
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* C. PROGRAM MANAGEMENT CONTACTS */}
      

      {/* D. FAIR OPPORTUNITY */}
      <section className="section-light-blue" aria-labelledby="fair-opportunity" data-id="element-1912" data-mp="2veq3agxb67zt">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1913" data-mp="2vcm3aa2j5t4t">
          <div className="max-w-3xl" data-id="element-1914" data-mp="15zsbfm1au1ac">
            <SectionHeading id="fair-opportunity" data-id="element-1915" data-mp="110p4tx5o7n09">
              Fair Opportunity and Requests for Quotes
            </SectionHeading>
            <div className="space-y-4 text-nrs-gray leading-relaxed" data-id="element-1916" data-mp="1qpmgc6lbj34u">
              {fairOpportunityIntro.map(p => <p key={p} data-id="element-1917" data-mp="1ur7s1om49bdg">{p}</p>)}
              <ol className="list-decimal list-outside pl-6 space-y-2" data-id="element-1918" data-mp="1us92prz7zvun">
                {fairOpportunityList.map(item => <li key={item} data-id="element-1919" data-mp="1zrlyx3bzef9w">{item}</li>)}
              </ol>
              {fairOpportunityRest.map(p => <p key={p} data-id="element-1920" data-mp="24m9fmg0qu5dx">{p}</p>)}
            </div>
          </div>
        </div>
      </section>

      {/* E. ORDERING & SUPPORT */}
      <section className="section-white" aria-labelledby="ordering-support" data-id="element-1921" data-mp="2dosg4th245x0">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1922" data-mp="2dmo39m16jwkl">
          <SectionHeading id="ordering-support" data-id="element-1923" data-mp="2igadz8ns9qdr">Ordering &amp; Support</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1924" data-mp="2ndir2uu2r485">
            {orderingSupport.map(item => <div key={item.title} className="card flex flex-col" data-id="element-1925" data-mp="2sf8064xql7tl">
                <h3 className="text-xl font-bold mb-3" data-id="element-1926" data-mp="2xci1ahuhn7x9">{item.title}</h3>
                <p className="text-nrs-gray leading-relaxed mb-4" data-id="element-1927" data-mp="2xanbap4d0pmb">{item.body}</p>
                <p className="text-sm font-bold text-nrs-navy mt-auto" data-id="element-1928" data-mp="lqz9akl7zby1">{item.contact}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* F. ORDERING GUIDE */}
      <section className="section-light-blue" aria-labelledby="ordering-guide" data-id="element-1929" data-mp="bs0bjx1zt0pt">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1930" data-mp="6tgotufkrkwe">
          <SectionHeading id="ordering-guide" data-id="element-1931" data-mp="14gp59b0py3rj">NRS SEWP VI Ordering Guide</SectionHeading>
          <div className="card flex flex-col md:flex-row md:items-center gap-8" data-id="element-1932" data-mp="zhcjktll32qj">
            <div className="flex items-start gap-4 flex-grow" data-id="element-1933" data-mp="zjf7n836elsm">
              <FileTextIcon className="h-10 w-10 text-nrs-navy flex-shrink-0" aria-hidden="true" data-id="element-1934" data-mp="up1gxizgl2ua" />
              <div data-id="element-1935" data-mp="pqhtfe57vizt">
                <h3 className="text-xl font-bold mb-3" data-id="element-1936" data-mp="pp6t5oazctdg">NRS SEWP VI Ordering Guide (PDF)</h3>
                <p className="text-sm font-bold text-nrs-navy mb-2" data-id="element-1937" data-mp="1n7yf5leeiujn">Contents:</p>
                <ul className="list-disc list-outside pl-5 text-nrs-gray space-y-1" data-id="element-1938" data-mp="1if5bz1ykzu9o">
                  {orderingGuideContents.map(c => <li key={c} data-id="element-1939" data-mp="qr2nd5fn75fx">{c}</li>)}
                </ul>
              </div>
            </div>
            <a href="https://jobs.newriversystems.com/wp-content/uploads/2026/10/New-River-Systems-SEWP-VI-Ordering-Guide.pdf" target="_blank" className={`btn-primary inline-flex items-center justify-center gap-2 flex-shrink-0 ${focusRing}`} data-id="element-1940" data-mp="vkdjocmtw3tl">
              <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1941" data-mp="1agmijd2t4f5h" />
              Download the Ordering Guide
            </a>
          </div>
        </div>
      </section>

      {/* G. CONTACT NASA SEWP */}
      <section className="section-white" aria-labelledby="contact-sewp" data-id="element-1942" data-mp="10iosck1e08a8">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1943" data-mp="10kjcvx8qxyds">
          <SectionHeading id="contact-sewp" data-id="element-1944" data-mp="15ff8f7d6z3vn">How to Contact NASA SEWP</SectionHeading>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1945" data-mp="3l7mogmtldqu2">
            <div className="flex gap-3" data-id="element-1946" data-mp="3q7r2853487gl">
              <ClockIcon className="h-5 w-5 text-nrs-navy mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-1947" data-mp="3q5ech5r48ssz" />
              <div data-id="element-1948" data-mp="3iq2ok4sntxqz">
                <dt className="font-bold text-nrs-navy" data-id="element-1949" data-mp="3dovh66ufj0ku">SEWP PMO Hours</dt>
                <dd className="text-nrs-gray" data-id="element-1950" data-mp="3dqzkamki7vk2">Mon–Fri, 7:30 AM – 6:00 PM (ET)</dd>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-1951" data-mp="3mpjx573dvb0d">
              <MailIcon className="h-5 w-5 text-nrs-navy mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-1952" data-mp="3mqmo1f0phdga" />
              <div data-id="element-1953" data-mp="2z10mikwv2m54">
                <dt className="font-bold text-nrs-navy" data-id="element-1954" data-mp="2z0igiwkectk0">Email</dt>
                <dd data-id="element-1955" data-mp="2uz6rot9ckccv">
                  <a href="mailto:help@sewp.nasa.gov" className={linkClass} data-id="element-1956" data-mp="2uzosm36yfer4">
                    help@sewp.nasa.gov
                  </a>
                </dd>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-1957" data-mp="33yaarx8ikrrd">
              <PhoneIcon className="h-5 w-5 text-nrs-navy mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-1958" data-mp="33x8w7isahn4w" />
              <div data-id="element-1959" data-mp="2g74w3leb85in">
                <dt className="font-bold text-nrs-navy" data-id="element-1960" data-mp="2baoaaxul238q">Customer Help Desk</dt>
                <dd data-id="element-1961" data-mp="2b8k2i4tkck36">
                  <a href="tel:+13012861478" className={linkClass} data-id="element-1962" data-mp="2tizdc968gsd9">
                    (301) 286-1478
                  </a>
                </dd>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-1963" data-mp="2jk9r647b0lr7">
              <GlobeIcon className="h-5 w-5 text-nrs-navy mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-1964" data-mp="2jj8iu7hoadd6" />
              <div data-id="element-1965" data-mp="2fhmzq4ejp2uj">
                <dt className="font-bold text-nrs-navy" data-id="element-1966" data-mp="2fiesl7grcmh4">Website</dt>
                <dd data-id="element-1967" data-mp="2akn28hvrh8f1">
                  <ExternalLink href="https://www.sewp.nasa.gov" data-id="element-1968" data-mp="2al6993jvicea">
                    NASA SEWP website
                  </ExternalLink>
                </dd>
              </div>
            </div>
          </dl>
        </div>
      </section>

      {/* H. RELATED LINKS */}
      <section className="section-light-blue" aria-labelledby="related-links" data-id="element-1969" data-mp="1vvgshzzarzsw">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1970" data-mp="1rsu9mokltp7u">
          <SectionHeading id="related-links" data-id="element-1971" data-mp="1rv6lql3ol5cz">Related Links</SectionHeading>
          <ul className="space-y-3" data-id="element-1972" data-mp="1muizsdub7epm">
            <li data-id="element-1973" data-mp="2bypguc19bgn">
              <ExternalLink href="https://www.sewp.nasa.gov/sewpvi/" data-id="element-1974" data-mp="2chvpdsbmbeu">
                NASA SEWP VI program site
              </ExternalLink>
            </li>
            <li data-id="element-1975" data-mp="798dq6norh2w">
              <Link to="/" className={linkClass} data-id="element-1976" data-mp="7cw6v4p7y1h3">
                New River Systems home page
              </Link>
            </li>
            <li data-id="element-1977" data-mp="gc9dlplykby4">
              <a href="https://jobs.newriversystems.com/wp-content/uploads/2026/10/New-River-Systems-SEWP-VI-Ordering-Guide.pdf" target="_blank" className={linkClass} data-id="element-1978" data-mp="gaoj1zzn8t09">
                NRS SEWP VI Ordering Guide (PDF)
              </a>
            </li>
          </ul>
        </div>
      </section>
    </div>;
}